import { useEffect, useRef } from "react";
import Reveal from "@/components/7fcc/Reveal";
import { VIDEO_TESTIMONIALS } from "@/data/content";

const LAYOUTS = {
  // 4-across desktop / 2x2 below — parent supplies the 1024px width.
  full: "grid w-full grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4",
  // 2 across the full 1024 container; stacks below 640px.
  pair: "mx-auto grid w-full max-w-[1024px] grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6",
  // 2 columns in an 840px centered container.
  squares: "mx-auto grid w-full max-w-[840px] grid-cols-2 gap-4 md:gap-6",
  // One frame filling its parent (e.g. /military spotlight card).
  single: "grid w-full grid-cols-1",
  // Five portraits: 5 across desktop, 3 tablet, 2 phone (odd last one centered).
  five: "grid w-full grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-5 max-sm:[&>*:last-child:nth-child(odd)]:col-span-2 max-sm:[&>*:last-child:nth-child(odd)]:mx-auto max-sm:[&>*:last-child:nth-child(odd)]:w-[calc(50%-0.5rem)]",
};

// Only one video plays at a time, across every slot on the page: playing
// one broadcasts a window event that pauses all the others.
function useOneAtATime(src) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const onPlay = () =>
      window.dispatchEvent(new CustomEvent("7fcc:video-play", { detail: { src } }));
    const pauseOthers = (e) => {
      if (e.detail?.src !== src) el?.pause();
    };
    el?.addEventListener("play", onPlay);
    window.addEventListener("7fcc:video-play", pauseOthers);
    return () => {
      el?.removeEventListener("play", onPlay);
      window.removeEventListener("7fcc:video-play", pauseOthers);
    };
  }, [src]);
  return ref;
}

function VideoFrame({ video, index, testIdPrefix }) {
  const ref = useOneAtATime(video.src);
  return (
    <figure className="m-0">
      <div
        className="relative w-full overflow-hidden rounded-lg border border-line bg-ink-950 shadow-panel"
        style={{ aspectRatio: video.aspect ?? "9 / 16" }}
        data-testid={`${testIdPrefix}-${index + 1}`}
      >
        <video
          ref={ref}
          src={video.src}
          controls
          playsInline
          preload="metadata"
          disablePictureInPicture
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          aria-label={video.alt}
          className="absolute inset-0 h-full w-full"
          style={{ objectFit: "contain" }}
        />
      </div>
      {video.caption ? (
        <figcaption
          className="mt-2 text-center text-xs leading-relaxed text-fg-muted"
          data-testid={`${testIdPrefix}-caption-${index + 1}`}
        >
          {video.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

// Phone-style vertical testimonial frames shared by /ready ("Wins In Their
// Own Words"), /apply ("Real Students") and /military. Layouts: full (4
// across), pair (2, stacks on mobile), single (one centered frame).
// `videos` defaults to the /ready list; campaign pages pass their own.
export default function VideoTestimonials({
  videos = VIDEO_TESTIMONIALS,
  layout = "full",
  testIdPrefix = "video-testimonial",
}) {
  return (
    <div className={LAYOUTS[layout] ?? LAYOUTS.full}>
      {videos.map((video, i) => (
        <Reveal key={video.src} delay={i * 0.08}>
          <VideoFrame video={video} index={i} testIdPrefix={testIdPrefix} />
        </Reveal>
      ))}
    </div>
  );
}
